import { ConsoleContinue } from "components/platform/companyConsole/ConsoleState";
import React from "react";
import { Alert, Box, Divider, Typography } from "@mui/material";
import { DataTable } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { COMPANY_JOURNAL } from "graphql/companyConsole";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import { Stack } from "components/layout/reusableLayouts";
import { ConsoleError } from "components/platform/companyConsole/ConsoleState";
import {
  JournalEntry,
  instant,
  label,
  money,
} from "components/platform/companyConsole/types";

export function JournalPosting({ entry }: { entry: JournalEntry }) {
  const { t } = useTranslation();
  return (
    <Stack gap="var(--space-3)">
      <Typography variant="subtitle2">
        {label(entry.type)} · {entry.sequence}
      </Typography>
      <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
        {entry.id}
      </Typography>
      <Typography variant="body2">
        {t("Effective")}: {instant(entry.effectiveAt)}
      </Typography>
      <Typography variant="body2">
        {t("Recorded")}: {instant(entry.recordedAt)}
      </Typography>
      <DataTable
        rows={entry.lines.map((line, index) => ({
          ...line,
          id: `${entry.id}:${index}`,
        }))}
        getRowKey={(line) => line.id}
        columns={[
          {
            id: "account",
            header: t("Ledger account"),
            cell: (line) => (
              <>
                {label(line.ledgerAccount)}
                <br />
                {line.currency}
              </>
            ),
          },
          {
            id: "debit",
            header: t("Debit"),
            align: "right",
            cell: (line) =>
              line.debit == null ? "" : money(line.debit, line.currency),
          },
          {
            id: "credit",
            header: t("Credit"),
            align: "right",
            cell: (line) =>
              line.credit == null ? "" : money(line.credit, line.currency),
          },
        ]}
      />
      {entry.references?.financialEventId && (
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {t("Financial event")}: {entry.references.financialEventId}
        </Typography>
      )}
      {entry.references?.authorizationId && (
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {t("Authorization")}: {entry.references.authorizationId}
        </Typography>
      )}
      {entry.references?.entryIds?.map((id) => (
        <Typography
          key={id}
          variant="caption"
          sx={{ overflowWrap: "anywhere" }}
        >
          {id}
        </Typography>
      ))}
    </Stack>
  );
}

export function TransactionJournal({
  companyId,
  transactionId,
}: {
  companyId: string;
  transactionId: string;
}) {
  const { t } = useTranslation();
  const page = useCompanyConnection<JournalEntry>({
    query: COMPANY_JOURNAL,
    variables: { companyId, transactionId },
    field: "companyJournal",
  });
  return (
    <Stack gap="var(--space-3)">
      <Typography variant="subtitle2">{t("Journal lines")}</Typography>
      {page.error && <ConsoleError retry={page.retry} />}
      <ConsoleContinue paused={page.paused} loadMore={page.loadMore} />
      {page.connection?.coverage === "NOT_COVERED" && (
        <Alert severity="info">
          {t("Journal lines are not available for this transaction")}
        </Alert>
      )}
      <Box
        role="region"
        aria-label={t("Journal lines")}
        tabIndex={0}
        ref={page.viewportRef}
        onScroll={page.handleScroll}
        sx={{ maxHeight: 480, overflow: "auto" }}
      >
        <Stack gap="var(--space-4)">
          {page.rows.map((entry) => (
            <React.Fragment key={entry.id}>
              <JournalPosting entry={entry} />
              <Divider />
            </React.Fragment>
          ))}
        </Stack>
        {(page.loading || page.isFetchingMore) && (
          <Typography role="status">{t("Loading journal lines")}</Typography>
        )}
        {!page.loading &&
          !page.rows.length &&
          page.connection?.coverage === "COVERED" && (
            <Typography>{t("No linked journal entries")}</Typography>
          )}
      </Box>
    </Stack>
  );
}
